import { Injectable } from "@angular/core";
import { collection, collectionData, Firestore, Query, query } from "@angular/fire/firestore";
import { Store } from "@ngrx/store";
import { IRngFreePlayConfig, ISngFreePlayConfig, IMttFreePlayConfig, TableType, GameMode, CurrencyType, RewardSystem } from "@soposopo/sopo-models";
import { Observable, Subscription } from "rxjs";
import { distinctUntilChanged, filter, map, takeUntil } from "rxjs/operators";
import { AppState } from "../app.state";
import { removeMttConfig, removeRingConfig, removeSngConfig, resetState, addMttConfig, addRingConfig, addSngConfig, rngConfigLoaded, sngConfigLoaded, mttConfigLoaded } from "./freePlayLobby.actions";
import { INIT_MTT_CONFIG, INIT_RING_CONFIG, INIT_SNG_CONFIG, MttConfig, RingConfig, SngConfig } from "./freePlayLobby.state";
import { FireSubService } from "../../firebase/fireSub.service";



@Injectable({
    providedIn: 'root'
})
export class FreePlayLobbyDispatcher {
    ringConfigSubscription: Subscription;
    sngConfigSubscription: Subscription;
    mttConfigSubscription: Subscription;

    lastRingConfigId = '';
    lastRingGameConfigIds: string[] = [];

    lastSngConfigId = '';
    lastSngGameConfigIds: string[] = [];

    lastMttConfigId = '';
    lastMttGameConfigIds: string[] = [];

    constructor(
        private store: Store<AppState>,
        private firestore: Firestore,
        private FireSubService: FireSubService,
    ) {}

    public subscribeToConfigs() {

        this.ringConfigSubscription = collectionData<IRngFreePlayConfig>(this.getConfigQuery(TableType.RING) as Query<IRngFreePlayConfig>)
            .subscribe((configArray) => {
                if (configArray.length === 0) return this.store.dispatch(rngConfigLoaded());
                let newGameConfigIds:string[] = [];
                configArray.forEach((game) => {
                    newGameConfigIds.push(game.gameConfigId);
                    if(!this.lastRingGameConfigIds.includes(game.gameConfigId)) 
                        this.store.dispatch(addRingConfig({ ringConfig: this.mapRingConfig(<IRngFreePlayConfig>game) }));
                });
                this.lastRingGameConfigIds.forEach(gameConfigId => {
                    if(!newGameConfigIds.includes(gameConfigId))
                        this.store.dispatch(removeRingConfig({ gameConfigId: gameConfigId }));
                });
                this.lastRingGameConfigIds = newGameConfigIds;
            });
        this.FireSubService.registerSubscription(this.ringConfigSubscription);

        this.sngConfigSubscription = collectionData<ISngFreePlayConfig>(this.getConfigQuery(TableType.SNG) as Query<ISngFreePlayConfig>)
            .subscribe((configArray) => {
                if (configArray.length === 0) return this.store.dispatch(sngConfigLoaded());
                let newGameConfigIds:string[] = [];
                configArray.forEach((game) => {
                    newGameConfigIds.push(game.gameConfigId);
                    if(!this.lastSngGameConfigIds.includes(game.gameConfigId)) 
                        this.store.dispatch(addSngConfig({ sngConfig: this.mapSngConfig(<ISngFreePlayConfig>game) }));
                });
                this.lastSngGameConfigIds.forEach(gameConfigId => {
                    if(!newGameConfigIds.includes(gameConfigId))
                        this.store.dispatch(removeSngConfig({ gameConfigId: gameConfigId }));
                });
                this.lastSngGameConfigIds = newGameConfigIds;
            });
        this.FireSubService.registerSubscription(this.sngConfigSubscription);

        this.mttConfigSubscription = collectionData<IMttFreePlayConfig>(this.getConfigQuery(TableType.MTT) as Query<IMttFreePlayConfig>)
            .subscribe((configArray) => {
                if (configArray.length === 0) return this.store.dispatch(mttConfigLoaded());
                let newGameConfigIds:string[] = [];
                configArray.forEach((game) => {
                    newGameConfigIds.push(game.gameConfigId);
                    if(!this.lastMttGameConfigIds.includes(game.gameConfigId)) 
                        this.store.dispatch(addMttConfig({ mttConfig: this.mapMttConfig(<IMttFreePlayConfig>game) }));
                });
                this.lastMttGameConfigIds.forEach(gameConfigId => {
                    if(!newGameConfigIds.includes(gameConfigId))
                        this.store.dispatch(removeMttConfig({ gameConfigId: gameConfigId }));
                });
                this.lastMttGameConfigIds = newGameConfigIds;
            });
        this.FireSubService.registerSubscription(this.mttConfigSubscription);
    }

    private getConfigQuery(tableType: TableType) {
        const collectionRef = collection(this.firestore, `/config/${tableType}/gameConfigs`);
        return query(collectionRef);
    }

    public getConfigs(tableType: TableType, gameMode: GameMode, currency: CurrencyType, rewardSystem: RewardSystem): Observable<(RingConfig | SngConfig| MttConfig)[]> {
        return this.store.select('freePlayLobby')
            .pipe(
                filter(freePlayLobbyState => !freePlayLobbyState.ringConfigs.loading && !freePlayLobbyState.mttConfigs.loading && !freePlayLobbyState.sngConfigs.loading),
                map(freePlayLobbyState => {
                    switch (tableType) {
                        case TableType.RING: {
                            return freePlayLobbyState.ringConfigs.configArray
                                .filter(config => config.gameMode.gameType === gameMode.gameType &&  config.gameMode.limitType === gameMode.limitType && config.currency === currency && config.rewardSystem === rewardSystem)
                                .sort((a,b) => parseFloat(a.blinds.split('/')[0]) - parseFloat(b.blinds.split('/')[0]));
                        }
                        case TableType.SNG: {
                            return freePlayLobbyState.sngConfigs.configArray
                            .filter(config => config.gameMode.gameType === gameMode.gameType &&  config.gameMode.limitType === gameMode.limitType && config.currency === currency && config.rewardSystem === rewardSystem)
                                .sort((a,b) => a.buyIn - b.buyIn);
                        }
                        case TableType.MTT: {
                            return freePlayLobbyState.mttConfigs.configArray
                            .filter(config => config.gameMode.gameType === gameMode.gameType &&  config.gameMode.limitType === gameMode.limitType && config.currency === currency && config.rewardSystem === rewardSystem)
                                .sort((a,b) => a.startingDate - b.startingDate);
                        }
                        default: {
                            return [];
                        }
                    }
                }),
                distinctUntilChanged(),
            )
    }

    public getRingConfigs(currency: CurrencyType, rewardSystem: RewardSystem): Observable<RingConfig[]> {
        return this.store.select('freePlayLobby')
            .pipe(
                filter(freePlayLobbyState => !freePlayLobbyState.ringConfigs.loading),
                map(freePlayLobbyState => freePlayLobbyState.ringConfigs.configArray
                    .filter(config => config.currency === currency && config.rewardSystem === rewardSystem)
                    .sort((a,b) => parseFloat(a.blinds.split('/')[0]) - parseFloat(b.blinds.split('/')[0]))
                ),
                distinctUntilChanged(),
            );
    }

    public destroySubscriptionsAndResetState(){
        this.destroySubscriptions();
        this.resetVariables();
        this.store.dispatch(resetState());
    }

    private mapRingConfig(config: IRngFreePlayConfig): RingConfig {
        const mappedConfig: RingConfig = {...INIT_RING_CONFIG};
        mappedConfig.type = TableType.RING;
        mappedConfig.tableName = config.settings.name;
        mappedConfig.gameConfigId = config.gameConfigId;
        mappedConfig.gameMode = config.rules.gameMode;
        mappedConfig.seats = config.rules.seats;
        mappedConfig.restricted = config.settings.restricted;

        mappedConfig.blinds = config.rules.blinds;
        mappedConfig.minBuyIn = config.rules.minBuyIn;
        mappedConfig.maxBuyIn = config.rules.maxBuyIn;
        mappedConfig.gpsRestriction = config.settings.gpsRestriction;
        mappedConfig.currency = config.currency || CurrencyType.GOLD;
        mappedConfig.rewardSystem = config.rules.rewardSystem ? config.rules.rewardSystem : RewardSystem.NONE;
        mappedConfig.badbeatJackpotEligible = config.rules.badbeatJackpotEligible ?? false;
        mappedConfig.bombPotConfig = config.rules.bombPotConfig ?? null;

        return mappedConfig;
    }

    private mapSngConfig(config: ISngFreePlayConfig): SngConfig {
        const mappedConfig: SngConfig = {...INIT_SNG_CONFIG};
        mappedConfig.type = TableType.SNG;
        mappedConfig.tableName = config.settings.name;
        mappedConfig.gameConfigId = config.gameConfigId;
        mappedConfig.gameMode = config.rules.gameMode;
        mappedConfig.seats = config.rules.seats;
        mappedConfig.restricted = config.settings.restricted;

        mappedConfig.gpsRestriction = config.settings.restricted;
        mappedConfig.buyIn = config.rules.buyIn;
        mappedConfig.currency = config.currency || CurrencyType.GOLD;
        mappedConfig.rewardSystem = config.rules.rewardSystem ? config.rules.rewardSystem : RewardSystem.NONE;

        return mappedConfig;
    }

    private mapMttConfig(config: IMttFreePlayConfig): MttConfig {
        let mappedConfig: MttConfig = {...INIT_MTT_CONFIG};
        mappedConfig.type = TableType.MTT
        mappedConfig.tableName = config.settings.name;
        mappedConfig.gameConfigId = config.gameConfigId;
        mappedConfig.gameMode = config.rules.gameMode;
        mappedConfig.seats = config.rules.maxPlayersPerTable;
        mappedConfig.restricted = config.settings.restricted;

        mappedConfig.blindsTime= config.rules.blindsTime;
        mappedConfig.startingDate = config.settings.startingDate/1000;
        mappedConfig.buyIn = config.rules.buyIn;
        mappedConfig.currency = config.currency || CurrencyType.SOPO_POINT;
        mappedConfig.rewardSystem = config.rules.rewardSystem ? config.rules.rewardSystem : RewardSystem.NONE;

        return mappedConfig;
    }

    private resetVariables() {
        this.lastRingConfigId = '';
        this.lastRingGameConfigIds = [];

        this.lastSngConfigId = '';
        this.lastSngGameConfigIds = [];

        this.lastMttConfigId = '';
        this.lastMttGameConfigIds = [];
    }

    private destroySubscriptions() {
        if (this.ringConfigSubscription) this.ringConfigSubscription.unsubscribe();
    }
}
